import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Check, Loader2, Search } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"

type LeafOption = {
  id: number
  canonical_name: string
  brand: string
  manufacturer: string
  pack_size: string | null
  star_product_code: string | null
  niq_key: string | null
  is_own: boolean
}

type AliasRow = {
  id: number
  alias_string: string
  source_kind: string
  source_kind_label: string
  dq_reason: string | null
  resolved_by: string
  default_scope: "own" | "all"
  leaf: LeafOption | null
}

type Props = {
  aliases: AliasRow[]
  pagination: { total_count: number; page: number; per_page: number }
  filters: { view: string; source_kind: string | null; dq_reason: string | null; search: string | null }
  filter_options: { source_kinds: { value: string; label: string }[]; dq_reasons: string[] }
  counts: { unresolved: number; unmatched: number; ambiguous: number; human_resolved: number }
}

const PATH = "/admin/product-aliases"
const DESCRIPTION =
  "Antrian data quality: setiap label produk yang tidak bisa dicocokkan otomatis oleh resolver deterministik. Selesaikan dengan menautkannya ke leaf yang benar."

const DQ_LABELS: Record<string, string> = { unmatched: "Tak ada kandidat", ambiguous: "Ambigu (>1)" }

function DqBadge({ reason }: { reason: string | null }) {
  if (!reason) return null
  return reason === "ambiguous" ? (
    <Badge tone="signal">{DQ_LABELS.ambiguous}</Badge>
  ) : (
    <Badge tone="muted">{DQ_LABELS.unmatched}</Badge>
  )
}

function pushQuery(next: Record<string, string | null | undefined>) {
  const q: Record<string, string> = {}
  Object.entries(next).forEach(([k, v]) => {
    if (v != null && v !== "") q[k] = String(v)
  })
  router.get(PATH, q, { preserveState: true, preserveScroll: true, replace: true })
}

// The resolve picker: a server-backed typeahead over the canonical leaves. The
// fetch runs only in effects/handlers (never during SSR/render).
function ResolveDialog({ alias }: { alias: AliasRow }) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState(alias.alias_string)
  const [includeCompetitors, setIncludeCompetitors] = React.useState(alias.default_scope === "all")
  const [results, setResults] = React.useState<LeafOption[]>([])
  const [loading, setLoading] = React.useState(false)
  const [selected, setSelected] = React.useState<LeafOption | null>(alias.leaf)
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    const term = query.trim()
    if (term.length < 2) {
      setResults([])
      return
    }
    setLoading(true)
    const controller = new AbortController()
    const timer = setTimeout(() => {
      const scope = includeCompetitors ? "all" : "own"
      fetch(`${PATH}/leaf_search?q=${encodeURIComponent(term)}&scope=${scope}`, {
        headers: { Accept: "application/json" },
        signal: controller.signal,
      })
        .then((r) => r.json())
        .then((data) => setResults(data.results ?? []))
        .catch(() => {
          /* aborted or network — ignore */
        })
        .finally(() => setLoading(false))
    }, 250)
    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [open, query, includeCompetitors])

  const submit = () => {
    if (!selected) return
    setSubmitting(true)
    router.patch(
      `${PATH}/${alias.id}`,
      { product_variant_id: selected.id },
      {
        preserveScroll: true,
        onSuccess: () => setOpen(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <>
      <Button size="sm" variant={alias.leaf ? "secondary" : "primary"} onClick={() => setOpen(true)}>
        {alias.leaf ? "Ubah" : "Selesaikan"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Selesaikan alias</DialogTitle>
            <DialogDescription>
              Tautkan label <strong>{alias.alias_string}</strong> ({alias.source_kind_label}) ke leaf kanonik
              yang benar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label htmlFor="leaf-q">Cari leaf</label>
              <div className="relative mt-1">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
                  aria-hidden="true"
                />
                <Input
                  id="leaf-q"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nama produk, kode STAR, atau NIQ key"
                  className="pl-9"
                  autoComplete="off"
                />
              </div>
              <label className="mt-2 flex items-center gap-2 font-normal text-ink-body">
                <input
                  type="checkbox"
                  checked={includeCompetitors}
                  onChange={(e) => setIncludeCompetitors(e.target.checked)}
                />
                Termasuk leaf kompetitor
              </label>
            </div>

            <div className="max-h-72 overflow-y-auto rounded-lg border border-hairline">
              {loading ? (
                <div className="flex items-center justify-center gap-2 p-6 text-sm text-ink-muted">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Mencari…
                </div>
              ) : results.length === 0 ? (
                <div className="p-6 text-center text-sm text-ink-muted">
                  {query.trim().length < 2 ? "Ketik minimal 2 karakter." : "Tidak ada leaf yang cocok."}
                </div>
              ) : (
                <ul>
                  {results.map((leaf) => {
                    const active = selected?.id === leaf.id
                    return (
                      <li key={leaf.id}>
                        <button
                          type="button"
                          onClick={() => setSelected(leaf)}
                          className={`flex w-full items-start justify-between gap-3 border-b border-hairline px-4 py-3 text-left last:border-b-0 hover:bg-surface ${
                            active ? "bg-surface" : ""
                          }`}
                          aria-pressed={active}
                        >
                          <span className="min-w-0">
                            <span className="block font-medium text-ink-body">{leaf.canonical_name}</span>
                            <span className="block text-xs text-ink-muted">
                              {leaf.brand} · {leaf.manufacturer}
                              {leaf.pack_size ? ` · ${leaf.pack_size}` : ""}
                              {leaf.star_product_code ? ` · ${leaf.star_product_code}` : ""}
                            </span>
                          </span>
                          <span className="flex shrink-0 items-center gap-2">
                            <Badge tone={leaf.is_own ? "accent" : "muted"}>
                              {leaf.is_own ? "Sendiri" : "Komp."}
                            </Badge>
                            {active && <Check className="h-4 w-4 text-accent" aria-hidden="true" />}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {selected && (
              <p className="text-sm text-ink-muted">
                Akan ditautkan ke: <strong className="text-ink-body">{selected.canonical_name}</strong>
                {alias.source_kind === "niq" && " (juga membuat tautan pasar own↔NIQ)"}.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Batal
            </Button>
            <Button onClick={submit} disabled={!selected || submitting}>
              {submitting ? "Menyimpan…" : "Tautkan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default function Index({ aliases, pagination, filters, filter_options, counts }: Props) {
  const resolvedView = filters.view === "resolved"
  const [search, setSearch] = React.useState(filters.search ?? "")
  const totalPages = Math.max(1, Math.ceil(pagination.total_count / pagination.per_page))

  const baseQuery = () => ({
    view: resolvedView ? "resolved" : null,
    source_kind: filters.source_kind,
    dq_reason: filters.dq_reason,
    search: filters.search,
  })

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    pushQuery({ ...baseQuery(), search })
  }

  return (
    <>
      <Head title="Antrian Review — Produk Master">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Antrian Review — Produk Master" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell width="wide">
        <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-ink-muted">
          <Link href="/admin/product-master" className="hover:text-ink-body">
            Produk Master
          </Link>
          <span>/</span>
          <span className="font-medium text-ink-body">Antrian Review</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <h1>Antrian Review</h1>
          <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
          {/* Surface toggle: alias DQ queue vs market-link audit */}
          <div className="mt-4 inline-flex rounded-lg border border-hairline p-1">
            <span className="rounded-md bg-surface px-3 py-1.5 text-sm font-medium text-ink-body">
              Antrian DQ ({counts.unresolved})
            </span>
            <Link
              href="/admin/product-market-links"
              className="rounded-md px-3 py-1.5 text-sm text-ink-muted hover:text-ink-body"
            >
              Tautan Pasar
            </Link>
          </div>
        </div>

        {/* View toggle: unresolved DQ vs human-resolved audit trail */}
        <div className="mt-6 flex flex-wrap gap-2">
          <Button
            variant={resolvedView ? "secondary" : "primary"}
            size="sm"
            onClick={() => pushQuery({ source_kind: filters.source_kind, search: filters.search })}
          >
            Belum terselesaikan ({counts.unresolved})
          </Button>
          <Button
            variant={resolvedView ? "primary" : "secondary"}
            size="sm"
            onClick={() => pushQuery({ view: "resolved", source_kind: filters.source_kind, search: filters.search })}
          >
            Selesai manual ({counts.human_resolved})
          </Button>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <form onSubmit={submitSearch} className="flex-1 min-w-[220px]">
            <label htmlFor="search">Cari label</label>
            <div className="relative mt-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
                aria-hidden="true"
              />
              <Input
                id="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Label alias"
                className="pl-9"
              />
            </div>
          </form>
          <div className="min-w-[170px]">
            <label htmlFor="source_kind">Sumber</label>
            <Select
              id="source_kind"
              className="mt-1"
              value={filters.source_kind ?? ""}
              onChange={(e) => pushQuery({ ...baseQuery(), source_kind: e.target.value })}
            >
              <option value="">Semua sumber</option>
              {filter_options.source_kinds.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>
          {!resolvedView && (
            <div className="min-w-[170px]">
              <label htmlFor="dq_reason">Alasan DQ</label>
              <Select
                id="dq_reason"
                className="mt-1"
                value={filters.dq_reason ?? ""}
                onChange={(e) => pushQuery({ ...baseQuery(), dq_reason: e.target.value })}
              >
                <option value="">Semua</option>
                {filter_options.dq_reasons.map((r) => (
                  <option key={r} value={r}>
                    {DQ_LABELS[r] ?? r}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>

        {/* Queue */}
        {aliases.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">
              {resolvedView ? "Belum ada alias yang diselesaikan manual." : "Tidak ada item di antrian. 🎉"}
            </p>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-ink-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">Label</th>
                  <th className="px-4 py-3 font-medium">Sumber</th>
                  <th className="px-4 py-3 font-medium">{resolvedView ? "Ditautkan ke" : "Alasan"}</th>
                  <th className="px-4 py-3 text-right font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {aliases.map((a) => (
                  <tr key={a.id} className="border-t border-hairline">
                    <td className="px-4 py-3 text-ink-body">{a.alias_string}</td>
                    <td className="px-4 py-3">
                      <Badge tone="neutral">{a.source_kind_label}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {resolvedView ? (
                        a.leaf ? (
                          <Link href={`/admin/product-master/${a.leaf.id}`} className="text-ink-body">
                            {a.leaf.canonical_name}
                          </Link>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )
                      ) : (
                        <DqBadge reason={a.dq_reason} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ResolveDialog alias={a} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between text-sm text-ink-muted">
            <span>
              Halaman {pagination.page} dari {totalPages} ({pagination.total_count})
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => pushQuery({ ...baseQuery(), page: String(pagination.page - 1) })}
              >
                Sebelumnya
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page >= totalPages}
                onClick={() => pushQuery({ ...baseQuery(), page: String(pagination.page + 1) })}
              >
                Berikutnya
              </Button>
            </div>
          </div>
        )}
      </AppShell>
    </>
  )
}
